import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api, classifyTrainType, trainTypeIcons, trainTypeColors, categoryLabels, attributeLabels } from './trainUtils';
import { getDelay, isDelayed, sortStations, interpolatePosition } from './trainPosition';
import BaureiheBadge from './BaureiheBadge';
import '../css/trainDetail.css';

// Auto-fit map bounds once on mount (not on every re-render)
const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  return null;
};

const TrainDetail = ({ trainUUID, currentStationUUID, mockupData }) => {
  const [train, setTrain] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stationCoords, setStationCoords] = useState(null);
  const [locationsJSON, setLocationsJSON] = useState(null);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [mapGrey, setMapGrey] = useState(false);
  const [simulatedNow, setSimulatedNow] = useState(null);
  const [activeTab, setActiveTab] = useState('route');
  const [capacityOverlay, setCapacityOverlay] = useState(0); // 0=off, 1=class1, 2=class2
  const detailRef = useRef(null);

  // Mockup time simulation: 1 real second = 2 simulated minutes
  useEffect(() => {
    if (!mockupData) { setSimulatedNow(null); return; }
    const stations = mockupData.train?.journey?.journeyStations || [];
    if (stations.length < 2) { setSimulatedNow(null); return; }
    const times = stations
      .map(s => s.departureTime_scheduled || s.arrivalTime_scheduled)
      .filter(Boolean)
      .map(t => new Date(t).getTime());
    if (times.length === 0) { setSimulatedNow(null); return; }
    const journeyStart = Math.min(...times);
    // Start 5 simulated minutes before first departure
    const startTime = journeyStart - 5 * 60000;
    setSimulatedNow(new Date(startTime));
    const speed = 2; // 1 real second = 2 simulated minutes
    const interval = setInterval(() => {
      setSimulatedNow(prev => prev ? new Date(prev.getTime() + speed * 60000) : null);
    }, 1000);
    return () => clearInterval(interval);
  }, [mockupData]);

  // Mockup mode: set data directly from props, skip API calls
  useEffect(() => {
    if (!mockupData) return;
    setTrain(mockupData.train);
    setLocationsJSON(mockupData.locationsJSON || null);
    setLoading(false);
    setError(null);
  }, [mockupData]);

  useEffect(() => {
    if (mockupData) return;
    if (!trainUUID) return;
    setLoading(true);
    setError(null);
    setLocationsJSON(null);
    api.get(`/v1/trains/${trainUUID}`)
      .then(res => {
        setTrain(res.data);
        // Fetch route geometry from separate endpoint
        const journeyUUID = res.data?.journey?.journeyUUID;
        if (journeyUUID) {
          setLocationsLoading(true);
          api.get(`/v1/journeys/${journeyUUID}/locations-json`)
            .then(locRes => setLocationsJSON(locRes.data))
            .catch(() => {})
            .finally(() => setLocationsLoading(false));
        }
      })
      .catch(err => {
        console.error('Error fetching train details:', err);
        setError('Zugdetails konnten nicht geladen werden.');
      })
      .finally(() => setLoading(false));
  }, [trainUUID, mockupData]);

  // Fetch station coordinates as fallback for the map
  useEffect(() => {
    if (mockupData) return;
    if (!currentStationUUID) return;
    api.get(`/v1/stations/${currentStationUUID}`)
      .then(res => {
        if (res.data && res.data.lat && res.data.lng) {
          setStationCoords(res.data);
        }
      })
      .catch(() => {});
  }, [currentStationUUID, mockupData]);

  if (loading) {
    return (
      <div className="train-detail-loading">
        <i className="fa fa-spinner fa-spin"></i> Laden...
      </div>
    );
  }

  if (error) {
    return <div className="train-detail-empty">{error}</div>;
  }

  if (!train) {
    return (
      <div className="train-detail-empty">
        Bitte Zug aus Liste auswählen
      </div>
    );
  }

  const type = classifyTrainType(train);
  const color = trainTypeColors[type];
  const icon = trainTypeIcons[type];
  const sr = train.stationRequest;
  const journey = train.journey;
  const stations = journey?.journeyStations || [];
  const attributes = journey?.journeyAttributes || [];
  const locations = journey?.journeyLocations || [];
  const messages = sr?.messages || [];
  const isCancelled = journey?.journeyCancelled === 1;

  const formatTime = (isoString) => {
    if (!isoString) return null;
    const d = new Date(isoString);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()} ${formatTime(isoString)}`;
  };

  // Sort stations chronologically (routeIdx is unreliable / often all 0)
  const sortedStations = sortStations(stations);
  const stationsWithCoords = sortedStations.filter(s => s.lat && s.lng);
  const firstStation = sortedStations[0];
  const lastStation = sortedStations[sortedStations.length - 1];

  // Build map markers: collect all stations with coords, then add key stations from fallback sources
  const mapMarkersMap = new Map();

  // Add all journeyStations that have coordinates
  stationsWithCoords.forEach(s => mapMarkersMap.set(s.stationUUID, s));

  // Ensure current station is on the map via stationRequest fallback
  if (sr && sr.lat && sr.lng && !mapMarkersMap.has(sr.stationUUID || currentStationUUID)) {
    mapMarkersMap.set(sr.stationUUID || currentStationUUID, {
      stationName: sr.stationName,
      stationUUID: sr.stationUUID || currentStationUUID,
      lat: sr.lat,
      lng: sr.lng,
      arrivalTime_scheduled: sr.arrivalTime_scheduled,
      arrivalTime_actual: sr.arrivalTime_actual,
      departureTime_scheduled: sr.departureTime_scheduled,
      departureTime_actual: sr.departureTime_actual,
      track_scheduled: sr.track_scheduled,
      track_actual: sr.track_actual,
    });
  }

  // Ensure current station is on the map via fetched station coords
  if (stationCoords && !mapMarkersMap.has(currentStationUUID)) {
    mapMarkersMap.set(currentStationUUID, {
      stationName: stationCoords.stationName || sr?.stationName,
      stationUUID: currentStationUUID,
      lat: stationCoords.lat,
      lng: stationCoords.lng,
      arrivalTime_scheduled: sr?.arrivalTime_scheduled,
      arrivalTime_actual: sr?.arrivalTime_actual,
      departureTime_scheduled: sr?.departureTime_scheduled,
      departureTime_actual: sr?.departureTime_actual,
      track_scheduled: sr?.track_scheduled,
      track_actual: sr?.track_actual,
    });
  }

  const mapMarkers = Array.from(mapMarkersMap.values());

  const polyCoords = locationsJSON?.polylineDescriptions
    ?.flatMap(p => p.coordinates?.map(c => [c.lat, c.lng]) || []) || [];
  const routeCoords = polyCoords.length > 0
    ? polyCoords
    : locations.length > 0
      ? locations.map(l => [l.lat, l.lng])
      : mapMarkers.length >= 2 ? mapMarkers.map(s => [s.lat, s.lng]) : [];

  // Estimate current train position by interpolating along the route
  const trainPositionData = interpolatePosition(
    stationsWithCoords,
    routeCoords,
    simulatedNow || new Date()
  );

  const rawPos = trainPositionData?.pos;
  const trainPosition = rawPos && isFinite(rawPos[0]) && isFinite(rawPos[1]) ? rawPos : null;
  const trainDelayed = trainPositionData?.delayed || false;
  const trainIconColor = trainDelayed ? '#e74c3c' : '#2ecc71';

  const trainIcon = L.divIcon({
    className: 'train-position-icon',
    html: `<div style="background:${trainIconColor};width:28px;height:28px;border-radius:50%;border:2px solid #fff;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.4)"><i class="fa fa-train" style="color:#fff;font-size:14px"></i></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });

  const hasMap = mapMarkers.length >= 1;

  const tabItems = [
    sortedStations.length > 0 && { id: 'route', icon: 'fa-road', label: 'Fahrtverlauf' },
    hasMap && { id: 'map', icon: 'fa-map', label: 'Karte' },
    attributes.length > 0 && { id: 'attributes', icon: 'fa-tags', label: 'Ausstattung' },
    messages.length > 0 && { id: 'messages', icon: 'fa-exclamation-triangle', label: 'Meldungen' },
  ].filter(Boolean);

  // Helper to render endpoint time info inline
  const renderEndpointTime = (station, timeField, label) => {
    const scheduled = station?.[`${timeField}_scheduled`];
    const actual = station?.[`${timeField}_actual`];
    if (!scheduled) return null;
    const delayed = isDelayed(scheduled, actual);
    const delay = getDelay(scheduled, actual);
    return (
      <span className="header-endpoint-time">
        {label} {formatTime(scheduled)}
        {actual && (
          <span className={`header-endpoint-actual ${delayed ? 'delayed' : 'on-time'}`}>
            {formatTime(actual)}{delay ? ` +${delay}` : ''}
          </span>
        )}
      </span>
    );
  };

  return (
    <div className="train-detail" ref={detailRef}>
      {/* Train image — uses expected series (fallback: first) */}
      {(() => {
        const series = train.trainSeries || [];
        const expected = series.find(s => s.trainSerieExpected === 1) || series[0];
        const uuid = expected?.trainSerieUUID || expected?.UUID;
        if (!uuid) return null;
        return (
          <img
            className="detail-train-image"
            src={`/trains/${uuid}.png`}
            alt={expected.trainSerieName || 'Zug'}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        );
      })()}
      {/* Header with train info + start/end */}
      <div className="detail-header" style={{ borderLeft: `6px solid ${color}` }}>
        <div className="detail-header-icon">{icon}</div>
        <div className="detail-header-info">
          <h3 className="detail-train-name">
            {train.trainName_long || train.trainName || train.trainTypeName || '?'}
            {isCancelled && <span className="detail-cancelled-badge" style={{ marginLeft: 10 }}>Fällt aus</span>}
          </h3>
          <div className="detail-meta">
            <span className="detail-badge" style={{ background: color }}>
              {categoryLabels[train.trainTypeProductCategorie] || train.trainTypeDescription || train.trainTypeName}
            </span>
            {train.trainLineName && (
              <span className="detail-line">Linie {train.trainLineName}</span>
            )}
            {train.trainNumber && (
              <span className="detail-number">Zugnr. {train.trainNumber}</span>
            )}
            {train.operatingDate && (
              <span className="detail-opdate">{train.operatingDate}</span>
            )}
          </div>
          {train.trainTerminus && (
            <div className="detail-terminus">
              <i className="fa fa-map-marker"></i> von {train.trainTerminus}
            </div>
          )}
          {train.trainSeries?.length > 0 && (
            <div className="detail-series">
              {[...train.trainSeries]
                .sort((a, b) => (b.trainSerieExpected === 1 ? 1 : 0) - (a.trainSerieExpected === 1 ? 1 : 0))
                .map((serie, i) => (
                <div key={i} className={`detail-serie-item ${serie.trainSerieExpected === 1 ? 'detail-serie-expected' : ''}`}>
                  <BaureiheBadge serie={serie} size="md" />
                  <span className="detail-serie-name">{serie.trainSerieName || serie.trainSerieNumber}</span>
                  {serie.trainSerieExpected === 1 && <span className="detail-serie-expected-badge">erwartet</span>}
                  {serie.trainSerieOperator && <span className="detail-serie-meta">{serie.trainSerieOperator}</span>}
                  {serie.trainSerieManufacturer && <span className="detail-serie-meta">{serie.trainSerieManufacturer}{serie.trainSerieManufacturerModelName ? ` ${serie.trainSerieManufacturerModelName}` : ''}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Start & End stations in header */}
      {(firstStation || lastStation) && (
        <div className="detail-header-endpoints">
          {firstStation && (
            <div className="header-endpoint">
              <div className="header-endpoint-name"><i className="fa fa-play"></i> {firstStation.stationName}</div>
              <div className="header-endpoint-details">
                {renderEndpointTime(firstStation, 'departureTime', 'Ab')}
                {firstStation.track_scheduled && (
                  <span className="header-endpoint-track">Gl. {firstStation.track_actual || firstStation.track_scheduled}</span>
                )}
              </div>
            </div>
          )}
          {firstStation && lastStation && <div className="header-endpoint-arrow"><i className="fa fa-long-arrow-right"></i></div>}
          {lastStation && (
            <div className="header-endpoint">
              <div className="header-endpoint-name"><i className="fa fa-flag-checkered"></i> {lastStation.stationName}</div>
              <div className="header-endpoint-details">
                {renderEndpointTime(lastStation, 'arrivalTime', 'An')}
                {lastStation.track_scheduled && (
                  <span className="header-endpoint-track">Gl. {lastStation.track_actual || lastStation.track_scheduled}</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      {tabItems.length > 0 && (
        <div className="detail-tabs">
          {tabItems.map(tab => (
            <button
              key={tab.id}
              className={`detail-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={`fa ${tab.icon}`}></i> {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Tab content */}
      <div className="detail-tab-content">
        {/* Route */}
        {activeTab === 'route' && sortedStations.length > 0 && (
          <div className="detail-section">
            <div className="detail-route">
              {sortedStations.map((stop, i) => {
                const isCurrent = stop.stationUUID === currentStationUUID;
                const stopArrDelay = getDelay(stop.arrivalTime_scheduled, stop.arrivalTime_actual);
                const stopDepDelay = getDelay(stop.departureTime_scheduled, stop.departureTime_actual);
                const stopIsDelayed = isDelayed(stop.arrivalTime_scheduled, stop.arrivalTime_actual)
                  || isDelayed(stop.departureTime_scheduled, stop.departureTime_actual);
                const hasActual = stop.arrivalTime_actual || stop.departureTime_actual;
                const delayClass = hasActual ? (stopIsDelayed ? 'stop-delayed' : 'stop-ontime') : '';
                return (
                  <div key={stop.UUID || i} className={`detail-stop ${isCurrent ? 'current-stop' : ''} ${delayClass}`}>
                    <div className="detail-stop-name">
                      {stop.stationName}
                      {stop.track_scheduled && (
                        <span className="detail-stop-track"> Gl. {stop.track_actual || stop.track_scheduled}</span>
                      )}
                      {(!!stop.capacityLevelClass1 || !!stop.capacityLevelClass2) && (
                        <div className="detail-stop-capacity">
                          {!!stop.capacityLevelClass1 && <span className={`capacity-dot capacity-${stop.capacityLevelClass1}`} title={`1. Klasse: Stufe ${stop.capacityLevelClass1}`}></span>}
                          {!!stop.capacityLevelClass2 && <span className={`capacity-dot capacity-${stop.capacityLevelClass2}`} title={`2. Klasse: Stufe ${stop.capacityLevelClass2}`}></span>}
                        </div>
                      )}
                    </div>
                    <div className="detail-stop-times">
                      {stop.arrivalTime_scheduled && (
                        <div className="stop-time-row">
                          <span className="stop-time-label">An</span>
                          <span className="stop-time-scheduled">{formatTime(stop.arrivalTime_scheduled)}</span>
                          {stop.arrivalTime_actual && (
                            <span className={`stop-time-actual ${isDelayed(stop.arrivalTime_scheduled, stop.arrivalTime_actual) ? 'delayed' : 'on-time'}`}>
                              {formatTime(stop.arrivalTime_actual)}
                              {stopArrDelay ? <span className="stop-delay">+{stopArrDelay}</span> : ''}
                            </span>
                          )}
                        </div>
                      )}
                      {stop.departureTime_scheduled && (
                        <div className="stop-time-row">
                          <span className="stop-time-label">Ab</span>
                          <span className="stop-time-scheduled">{formatTime(stop.departureTime_scheduled)}</span>
                          {stop.departureTime_actual && (
                            <span className={`stop-time-actual ${isDelayed(stop.departureTime_scheduled, stop.departureTime_actual) ? 'delayed' : 'on-time'}`}>
                              {formatTime(stop.departureTime_actual)}
                              {stopDepDelay ? <span className="stop-delay">+{stopDepDelay}</span> : ''}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {stations.some(s => s.capacityLevelClass1 || s.capacityLevelClass2) && (
              <div className="capacity-legend">
                <span className="capacity-legend-title">Auslastung:</span>
                <span className="capacity-legend-item"><span className="capacity-dot capacity-1"></span> Gering</span>
                <span className="capacity-legend-item"><span className="capacity-dot capacity-2"></span> Mittel</span>
                <span className="capacity-legend-item"><span className="capacity-dot capacity-3"></span> Hoch</span>
                <span className="capacity-legend-item"><span className="capacity-dot capacity-4"></span> Sehr hoch</span>
              </div>
            )}
          </div>
        )}

        {/* Map */}
        {activeTab === 'map' && hasMap && (
          <div className="detail-section detail-section-map">
            <div className="detail-map-toolbar">
              {locationsLoading && <span className="detail-map-loading"><i className="fa fa-spinner fa-spin"></i> Route laden...</span>}
              <button className="detail-map-style-btn" onClick={() => setMapGrey(g => !g)} title={mapGrey ? 'Farbig' : 'Graustufen'}>
                <i className={`fa ${mapGrey ? 'fa-paint-brush' : 'fa-adjust'}`}></i> {mapGrey ? 'Farbig' : 'Graustufen'}
              </button>
              <button
                className={`detail-map-style-btn ${capacityOverlay > 0 ? 'active' : ''}`}
                onClick={() => setCapacityOverlay(c => (c + 1) % 3)}
                title="Auslastung anzeigen"
              >
                <i className="fa fa-users"></i>
                {capacityOverlay === 0 ? 'Auslastung' : capacityOverlay === 1 ? '1. Klasse' : '2. Klasse'}
              </button>
            </div>
            <div className="detail-map">
              <MapContainer
                center={[mapMarkers[0].lat, mapMarkers[0].lng]}
                zoom={mapMarkers.length === 1 ? 13 : 8}
                scrollWheelZoom={true}
                style={{ height: '100%', width: '100%' }}
              >
                {mapGrey ? (
                  <TileLayer
                    key="grey"
                    attribution='&copy; <a href="https://carto.com/">CARTO</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  />
                ) : (
                  <TileLayer
                    key="color"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                )}
                <TileLayer
                  attribution='&copy; <a href="https://www.openrailwaymap.org/">OpenRailwayMap</a>'
                  url="https://tiles.openrailwaymap.org/standard/{z}/{x}/{y}.png"
                  opacity={0.7}
                />
                {routeCoords.length >= 2 && capacityOverlay === 0 && (
                  <Polyline positions={routeCoords} color={color} weight={3} opacity={0.8} />
                )}
                {routeCoords.length >= 2 && capacityOverlay > 0 && stationsWithCoords.length >= 2 && (() => {
                  // Capacity colors
                  const capacityColors = { 1: '#2ecc71', 2: '#f0b429', 3: '#f85149', 4: '#da3633' };
                  // Find nearest point on routeCoords for each station
                  const findNearest = (lat, lng) => {
                    let minDist = Infinity, minIdx = 0;
                    for (let j = 0; j < routeCoords.length; j++) {
                      const d = (routeCoords[j][0] - lat) ** 2 + (routeCoords[j][1] - lng) ** 2;
                      if (d < minDist) { minDist = d; minIdx = j; }
                    }
                    return minIdx;
                  };
                  const segments = [];
                  for (let i = 0; i < stationsWithCoords.length - 1; i++) {
                    const from = stationsWithCoords[i];
                    const to = stationsWithCoords[i + 1];
                    const fromIdx = findNearest(from.lat, from.lng);
                    const toIdx = findNearest(to.lat, to.lng);
                    if (fromIdx >= toIdx) continue;
                    const segCoords = routeCoords.slice(fromIdx, toIdx + 1);
                    const capLevel = capacityOverlay === 1 ? from.capacityLevelClass1 : from.capacityLevelClass2;
                    const segColor = capacityColors[capLevel] || '#888';
                    segments.push({ positions: segCoords, color: segColor, key: `${from.stationUUID}-${to.stationUUID}` });
                  }
                  return segments.map(s => (
                    <Polyline key={s.key} positions={s.positions} color={s.color} weight={5} opacity={0.85} />
                  ));
                })()}
                {mapMarkers.map((stop, i) => {
                  const isCurrent = stop.stationUUID === currentStationUUID;
                  const isFirst = stop.stationUUID === firstStation?.stationUUID;
                  const isLast = stop.stationUUID === lastStation?.stationUUID;
                  const stopArrDelay = getDelay(stop.arrivalTime_scheduled, stop.arrivalTime_actual);
                  const stopDepDelay = getDelay(stop.departureTime_scheduled, stop.departureTime_actual);
                  const stopIsDelayed = isDelayed(stop.arrivalTime_scheduled, stop.arrivalTime_actual)
                    || isDelayed(stop.departureTime_scheduled, stop.departureTime_actual);
                  const hasActualTime = stop.arrivalTime_actual || stop.departureTime_actual;
                  const dotColor = hasActualTime ? (stopIsDelayed ? '#e74c3c' : '#2ecc71') : color;

                  const popupContent = (
                    <Popup>
                      <strong>{stop.stationName}</strong>
                      {stop.arrivalTime_scheduled && (
                        <>
                          <br />An: {formatTime(stop.arrivalTime_scheduled)}
                          {stop.arrivalTime_actual && (
                            <span style={{ color: isDelayed(stop.arrivalTime_scheduled, stop.arrivalTime_actual) ? '#e74c3c' : '#2ecc71', fontWeight: 600, marginLeft: 4 }}>
                              → {formatTime(stop.arrivalTime_actual)}
                              {stopArrDelay ? ` (+${stopArrDelay})` : ''}
                            </span>
                          )}
                        </>
                      )}
                      {stop.departureTime_scheduled && (
                        <>
                          <br />Ab: {formatTime(stop.departureTime_scheduled)}
                          {stop.departureTime_actual && (
                            <span style={{ color: isDelayed(stop.departureTime_scheduled, stop.departureTime_actual) ? '#e74c3c' : '#2ecc71', fontWeight: 600, marginLeft: 4 }}>
                              → {formatTime(stop.departureTime_actual)}
                              {stopDepDelay ? ` (+${stopDepDelay})` : ''}
                            </span>
                          )}
                        </>
                      )}
                      {stop.track_scheduled && <><br />Gl. {stop.track_actual || stop.track_scheduled}</>}
                    </Popup>
                  );

                  if (isFirst || isLast) {
                    const faIcon = isFirst ? 'fa-play' : 'fa-flag-checkered';
                    const stationIcon = L.divIcon({
                      className: 'station-endpoint-icon',
                      html: `<div style="background:${dotColor}"><i class="fa ${faIcon}"></i></div>`,
                      iconSize: [24, 24],
                      iconAnchor: [12, 12],
                    });
                    return (
                      <Marker key={stop.UUID || i} position={[stop.lat, stop.lng]} icon={stationIcon}>
                        {popupContent}
                      </Marker>
                    );
                  }

                  return (
                    <CircleMarker
                      key={stop.UUID || i}
                      center={[stop.lat, stop.lng]}
                      radius={isCurrent ? 10 : 5}
                      fillColor={dotColor}
                      color="#fff"
                      weight={isCurrent ? 3 : 2}
                      fillOpacity={1}
                    >
                      {popupContent}
                    </CircleMarker>
                  );
                })}
                {trainPosition && isFinite(trainPosition[0]) && isFinite(trainPosition[1]) && (
                  <Marker position={trainPosition} icon={trainIcon}>
                    <Popup>
                      <strong>{train.trainName_long || train.trainName}</strong>
                      <br />Aktuelle Position (geschätzt)
                    </Popup>
                  </Marker>
                )}
                {mapMarkers.length >= 2 && (
                  <FitBounds bounds={mapMarkers.map(s => [s.lat, s.lng])} />
                )}
              </MapContainer>
            </div>
          </div>
        )}

        {/* Attributes */}
        {activeTab === 'attributes' && attributes.length > 0 && (
          <div className="detail-section">
            <div className="detail-attributes">
              {attributes.map((attr, i) => {
                const mapped = attributeLabels[attr.categorieKey];
                return (
                  <span key={attr.UUID || i} className="detail-attribute">
                    <i className={`fa ${mapped?.icon || 'fa-info-circle'}`}></i>
                    {mapped?.label || attr.categorieValue || attr.categorieKey}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Messages */}
        {activeTab === 'messages' && messages.length > 0 && (
          <div className="detail-section">
            {messages.map((msg, i) => (
              <div key={msg.UUID || i} className={`detail-message ${msg.priority >= 2 ? 'priority-high' : ''}`}>
                <div className="detail-message-text">{msg.messageText}</div>
                <div className="detail-message-time">{formatDateTime(msg.created)}</div>
                {msg.messageURL && (
                  <a href={msg.messageURL} target="_blank" rel="noopener noreferrer" className="detail-message-link">
                    Mehr erfahren <i className="fa fa-external-link"></i>
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainDetail;
